import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

/**
 * 参数示例:
 * ```typescript
 * {
 *   "prompt": "A beautiful landscape with mountains and a lake at sunset, photorealistic, 4k quality",
 *   "width": 1024,
 *   "height": 768,
 *   "steps": 40,
 *   "prompt_upsampling": false,
 *   "seed": 42,
 *   "guidance": 2.5,
 *   "safety_tolerance": 2,
 *   "interval": 2,
 *   "output_format": "jpeg"
 * }
 * ```
 */
export interface BlackforestlabsFlux1ProParams {
  /**
   * 文本提示词，描述要生成的图像内容，可选，默认为空字符串
   * @default ""
   */
  prompt?: string;
  /**
   * 图像提示，必须是有效的Base64编码图像，用作图像生成的参考
   */
  image_prompt?: string;
  /**
   * 图像宽度（像素），必须是32的倍数，范围256-1440，默认1024
   * @default 1024
   * @range 256 - 1440
   */
  width?: number;
  /**
   * 图像高度（像素），必须是32的倍数，范围256-1440，默认768
   * @default 768
   * @range 256 - 1440
   */
  height?: number;
  /**
   * 生成步数，控制生成质量和时间，范围1-50，默认40
   * @default 40
   * @range 1 - 50
   */
  steps?: number;
  /**
   * 是否启用提示词增强，开启后会自动优化和扩展提示词，默认false
   * @default false
   */
  prompt_upsampling?: boolean;
  /**
   * 随机种子，用于生成可重复的结果
   */
  seed?: number;
  /**
   * 引导强度，控制生成结果与提示词的贴合度，范围1.5-5，默认2.5
   * @default 2.5
   * @range 1.5 - 5
   */
  guidance?: number;
  /**
   * 安全容忍度，控制内容安全过滤级别，范围0-6，0最严格，6最宽松，默认2
   * @default 2
   * @range 0 - 6
   */
  safety_tolerance?: number;
  /**
   * 间隔参数，范围1-4，默认2
   * @default 2
   * @range 1 - 4
   */
  interval?: number;
  /**
   * 输出格式，可选jpeg或png，默认jpeg
   * @default "jpeg"
   * @values jpeg | png
   */
  output_format?: 'jpeg' | 'png';
  /**
   * 回调URL，生成完成后会向此URL发送通知，必须以http://或https://开头，长度1-2083字符
   */
  webhook_url?: string;
  /**
   * 回调密钥，用于验证webhook请求的安全性
   */
  webhook_secret?: string;
}

export interface BlackforestlabsFlux1ProResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * blackforestlabs_flux_1_pro
 *
 * Black Forest Labs FLUX.1 [pro] 图像生成接口，支持文本提示词和图像提示生成高质量图像。
 * 
 * Model: flux-pro
 * Features:
 * - 高质量图像生成
 * - 支持文本提示词和图像提示
 * - 可自定义图像尺寸（必须是32的倍数）
 * - 支持提示词自动增强
 * - 可控生成步数和引导强度
 * - 可选安全过滤级别
 *
 * @example
 * ```typescript
 * import { initSeacloud, blackforestlabsFlux1Pro } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await blackforestlabsFlux1Pro(
 *   {
 *     "prompt": "A beautiful landscape with mountains and a lake at sunset, photorealistic, 4k quality",
 *     "width": 1024,
 *     "height": 768,
 *     "steps": 40,
 *     "prompt_upsampling": false,
 *     "seed": 42,
 *     "guidance": 2.5,
 *     "safety_tolerance": 2,
 *     "interval": 2,
 *     "output_format": "jpeg"
 *   }
 * );
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 BlackforestlabsFlux1ProParams 接口定义
 * @returns 生成的资源列表
 */
export async function blackforestlabsFlux1Pro(
  params: BlackforestlabsFlux1ProParams,
): Promise<BlackforestlabsFlux1ProResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'blackforestlabs_flux_1_pro',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: BlackforestlabsFlux1ProResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
